/**
 * What the loader did, and what it would not accept.
 *
 * Every delivery has been recorded since M2 — a batch, a row per workbook, a row per
 * finding — and until the ingest router shipped, none of it could be read without a database
 * session. That is the wrong shape for the question, because the person who has to fix a
 * rejection is a project manager looking at their own workbook, not somebody with SQL.
 *
 * So the screen is organised around the twelve files that need somebody to act, not the four
 * hundred and eighty-eight that worked. The counts are at the top because "did it work" is
 * the first question; the quarantine is the body because "what do I do about it" is the only
 * one with an answer that costs anybody time.
 *
 * Findings carry the offending value, and showing it is the point. "Invalid date in row 41"
 * is a support ticket. "Row 41, Target date, 31/02/2026" is a thirty-second fix by the
 * person who typed it.
 *
 * Nothing here starts a load. A run is minutes of CPU across hundreds of files; a button that
 * begins one either blocks until it times out or lies about having finished. Deliveries are
 * started by `scripts/ingest.py`, which queues them, and this screen watches.
 */

import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import { Empty, Failed, Skeleton } from '@/components/states'
import { formatCount, formatDate } from '@/lib/format'

type Batch = {
  id: string
  reference: string
  source: string
  status: string
  files_expected: number | null
  files_loaded: number
  files_quarantined: number
  files_waiting: number
  rows_loaded: number
  started_at: string | null
  finished_at: string | null
}

type FileOutcome = {
  id: string
  path: string
  project_number: string | null
  package_ref: string | null
  status: string
  rows_loaded: number
  error_count: number
}

type BatchDetail = Batch & { files: FileOutcome[] }

type Issue = {
  id: string
  file_id: string
  sheet: string | null
  row_number: number | null
  column_name: string | null
  severity: string
  code: string | null
  message: string
  raw_value: string | null
  suggested_value: string | null
  resolved: boolean
}

/** A workbook's own name, without the path it arrived under.
 *
 * The full path is a local folder on somebody's machine and says nothing a reader needs. */
function filename(path: string): string {
  const parts = path.split(/[\\/]/)
  return parts[parts.length - 1] || path
}

export function Ingestion() {
  const { t, i18n } = useTranslation()
  const [chosen, setChosen] = useState<string | null>(null)

  const batches = useQuery({
    queryKey: ['ingest', 'batches'],
    queryFn: ({ signal }) => api.get<Batch[]>('/ingest/batches?limit=20', signal),
    // A load takes minutes and this is the screen somebody leaves open while it runs.
    refetchInterval: 30_000,
  })

  const current = chosen ?? batches.data?.[0]?.id ?? null

  const detail = useQuery({
    queryKey: ['ingest', 'batch', current],
    queryFn: ({ signal }) => api.get<BatchDetail>(`/ingest/batches/${current}`, signal),
    enabled: Boolean(current),
    refetchInterval: 30_000,
  })

  const issues = useQuery({
    queryKey: ['ingest', 'issues', current],
    queryFn: ({ signal }) =>
      api.get<Issue[]>(`/ingest/batches/${current}/issues?resolved=false`, signal),
    enabled: Boolean(current),
  })

  if (batches.isError) {
    const error = batches.error
    return (
      <main className="page">
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => void batches.refetch()}
        />
      </main>
    )
  }

  const all = batches.data ?? []
  const batch = detail.data ?? null

  return (
    <main className="page" data-testid="ingestion">
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('nav.ingestion')}</h1>
          <p className="page__subtitle">{t('ingest.subtitle')}</p>
        </div>
      </header>

      {batches.isPending ? (
        <Skeleton rows={4} />
      ) : all.length === 0 ? (
        // Nothing has ever been loaded is a real state, and it was the true state of this
        // product until the loader got a caller. It says how to start one rather than
        // leaving somebody looking for a button that is deliberately not here.
        <Empty title={t('ingest.none')} hint={t('ingest.noneHint')} />
      ) : (
        <>
          <section className="card card--pad" data-testid="ingest-batch">
            <h2 className="card__title">
              {batch?.reference ?? all[0]!.reference}{' '}
              <span className={`chip chip--${batch?.status ?? all[0]!.status}`}>
                {t(`ingest.status.${batch?.status ?? all[0]!.status}`, {
                  defaultValue: batch?.status ?? all[0]!.status,
                })}
              </span>
            </h2>
            <p className="card__caption">
              {t('ingest.startedAt', {
                when: formatDate(batch?.started_at ?? all[0]!.started_at, i18n.language),
              })}
            </p>

            {batch ? (
              <>
                <div className="counts" data-testid="ingest-counts">
                  <Count
                    value={batch.files_loaded}
                    label={t('ingest.loaded')}
                    tone="good"
                    locale={i18n.language}
                  />
                  <Count
                    value={batch.files_quarantined}
                    label={t('ingest.quarantined')}
                    tone="bad"
                    locale={i18n.language}
                  />
                  <Count
                    value={batch.files_waiting}
                    label={t('ingest.waiting')}
                    tone="steel"
                    locale={i18n.language}
                  />
                  <Count
                    value={batch.rows_loaded}
                    label={t('ingest.rows')}
                    tone="steel"
                    locale={i18n.language}
                  />
                </div>

                {/* Three segments of one bar rather than a percentage. "88% complete" over a
                    delivery with twelve quarantined files hides the only part anybody has to
                    do something about. */}
                <div
                  className="segments"
                  role="img"
                  aria-label={t('ingest.progressLabel', {
                    loaded: batch.files_loaded,
                    quarantined: batch.files_quarantined,
                    expected: batch.files_expected ?? 0,
                  })}
                >
                  <span
                    className="segments__part segments__part--loaded"
                    style={{ inlineSize: `${share(batch.files_loaded, batch.files_expected)}%` }}
                  />
                  <span
                    className="segments__part segments__part--quarantined"
                    style={{
                      inlineSize: `${share(batch.files_quarantined, batch.files_expected)}%`,
                    }}
                  />
                </div>
              </>
            ) : (
              <Skeleton rows={2} />
            )}
          </section>

          <section className="card card--pad" data-testid="ingest-quarantine">
            <h2 className="card__title">{t('ingest.quarantine')}</h2>
            <p className="card__caption card__caption--note">{t('ingest.quarantineNote')}</p>

            {issues.isPending ? (
              <Skeleton rows={3} />
            ) : (issues.data ?? []).length === 0 ? (
              <Empty title={t('ingest.clean')} />
            ) : (
              <div className="grid__scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('ingest.file')}</th>
                      <th scope="col">{t('ingest.where')}</th>
                      <th scope="col">{t('ingest.problem')}</th>
                      <th scope="col">{t('ingest.value')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(issues.data ?? []).map((issue) => (
                      <tr key={issue.id} data-testid="ingest-issue" data-severity={issue.severity}>
                        <td className="numeric">{fileOf(batch, issue.file_id)}</td>
                        <td>
                          {[issue.sheet, issue.row_number ? `row ${issue.row_number}` : null,
                            issue.column_name]
                            .filter(Boolean)
                            .join(' · ')}
                        </td>
                        <td>{issue.message}</td>
                        {/* The value that caused it, verbatim. Anything else here and the
                            reader has to open the workbook to find out what to change. */}
                        <td className="numeric">
                          {issue.raw_value ? <code>{issue.raw_value}</code> : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card card--pad" data-testid="ingest-batches">
            <h2 className="card__title">{t('ingest.previous')}</h2>
            <div className="grid__scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('ingest.reference')}</th>
                    <th scope="col">{t('ingest.started')}</th>
                    <th scope="col" className="table__numeric">{t('ingest.loaded')}</th>
                    <th scope="col" className="table__numeric">{t('ingest.quarantined')}</th>
                    <th scope="col" className="table__numeric">{t('ingest.rows')}</th>
                  </tr>
                </thead>
                <tbody>
                  {all.map((row) => (
                    <tr
                      key={row.id}
                      data-testid="ingest-batch-row"
                      data-current={row.id === current ? 'true' : undefined}
                    >
                      <td className="numeric">
                        <button
                          type="button"
                          className="link"
                          onClick={() => setChosen(row.id)}
                          data-testid="ingest-batch-open"
                        >
                          {row.reference}
                        </button>
                      </td>
                      <td>{formatDate(row.started_at, i18n.language)}</td>
                      <td className="table__numeric numeric">
                        {formatCount(row.files_loaded, i18n.language)}
                      </td>
                      <td className="table__numeric numeric">
                        {formatCount(row.files_quarantined, i18n.language)}
                      </td>
                      <td className="table__numeric numeric">
                        {formatCount(row.rows_loaded, i18n.language)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  )
}

/** A segment's width, guarding the delivery whose expected count is unknown or zero. */
function share(part: number, total: number | null): number {
  if (!total || total <= 0) return 0
  return Math.min(100, Math.round((100 * part) / total))
}

/** The workbook a finding belongs to, by name.
 *
 * Falls back to the id rather than to nothing: an unmatched finding is a bug worth being
 * able to see, and a blank cell hides it. */
function fileOf(batch: BatchDetail | null, fileId: string): string {
  const found = batch?.files.find((file) => file.id === fileId)
  return found ? filename(found.path) : fileId.slice(0, 8)
}

function Count({
  value,
  label,
  tone,
  locale,
}: {
  value: number
  label: string
  tone: string
  locale: string
}) {
  return (
    <div className="count" data-tone={tone}>
      {/* formatCount, not value.toLocaleString(): the bare call formats to the *browser's*
          locale, so an Arabic page on an English machine printed Latin digits in a column of
          Arabic-Indic ones. */}
      <p className="count__value numeric">{formatCount(value, locale)}</p>
      <p className="count__label">{label}</p>
    </div>
  )
}
